import { curry } from 'lib/util'
import { showBlockingDialog } from 'app/_shared/events/dialog'
import session from 'app/_shared/session'
import { searchReferenceMeasurement } from 'app/xEcoApm/action/measurement/detail'

type Measurement = {
  Rawdata: {
    Frequency: number[],
    Cavitation: number[]
  },
  Results: {
    Frequency: number[],
    APM: number[],
    Time: number[],
  },
}

type Information = {
  Serial: string,
  Customer: string,
  Date: string,
  Time: string,
  User: string,
  Machine: string,
  HWL: number,
  Tank: string,
  Location: string,
  Frequency: string,
  Power: string,
  Temperature: number,
  Liquid: string,
  Characterization: string,
}

type ReportExportRequest = {
  Measurement: Measurement,
  Information: Information,
}

/**
 * Displays an error informing the user that an error has occurred while exporting the report.
 * @param module  current module
 */
const showErrorDialog = (module: Gaia.Component.Spec) => showBlockingDialog(module, null, {
  title: {
    ns: 'apmGo',
    key: 'dialog.exportReportError.title',
    defaultValue: 'Error',
  },
  text: {
    ns: 'apmGo',
    key: 'dialog.exportReportError.text',
    defaultValue: 'An error has occurred while attempting to export the report. Please try again later.',
  },
  cancel: false,
})

/**
 * Generates a filename from `request` data.
 *
 * @param exportRequest         export payload
 * @param exportRequest.Current current measurement data
 */
const getFilename = ({ Current: { Information: info } }: { Current: ReportExportRequest}) => {
  const { Date: date, Tank: tank, Location: location, Frequency: frequency, Power: power } = info
  const reference = info.Characterization === 'True'

  const fileDate = date.replace(/[/.-]/g, '_')
  const type = reference ? 'Calibration' : 'Verification'

  return `${fileDate}_${type}_${tank}_${location}_${frequency}_${power}.pdf`
}

/**
 * Maps a `measurement` document to the structure expected by the server.
 *
 * @param module      current module
 * @param measurement mapping target
 */
const map = (
  module: Gaia.AppModule.Spec,
  measurement: any,
): ReportExportRequest => {
  const { value = {}, refs = {} } = measurement
  const currentUser = session(module).user.ref()
  const data = JSON.parse(value.measuredData)
  const user = refs.submitter?.[0].value.name || currentUser.value.name
  const date = value.submit?.timestamp
    ? new Date(value.submit.timestamp)
    : new Date()

  const temperature = value.temperatureUnit === 'fahrenheit'
    ? (parseFloat(value.temperature) - 32) / 1.8
    : parseFloat(value.temperature)

  return {
    Measurement: {
      Rawdata: {
        Frequency: data.rawdata.frequency,
        Cavitation: data.rawdata.cavitation,
      },
      Results: {
        Frequency: data.results.frequency,
        APM: data.results.apm,
        Time: data.results.time,
      }
    },
    Information: {
      Serial: value.serial,
      Customer: value.customer,
      Date: module[G.ADAPTER][G.INTL].getLocaleDate(date, { dateStyle: 'short' }),
      Time: module[G.ADAPTER][G.INTL].getLocaleTime(date, { hour12: false }),
      User: user,
      Machine: value.project,
      HWL: 0,
      Tank: String(value.tank),
      Location: value.location,
      Frequency: String(value.frequency),
      Power: String(value.usPower),
      Temperature: temperature,
      Liquid: value.cleaningAgent,
      Characterization: value.reference ? 'True' : 'False',
    }
  }
}

/**
 * Requests the server to generate a PDF document from the current measurement data.
 *
 * @param module    current module
 * @param component source quick action
 * @param event     triggered event
 */
const exportReport = async (
  module: Gaia.AppModule.Spec,
  component: { loading: boolean },
  event: Gaia.PlatformEvent,
)=> {
  const eventBus = module[G.ADAPTER][G.EVENTS]
  const { measurement } = module[G.MODEL][G.CHILDREN]
  const { version } = measurement[G.PROPS]
  const measurementData = measurement[G.CACHE]

  component.loading = true
  await module[G.ADAPTER][G.UI].update(module)

  try {
    const referenceMeasurementData = await searchReferenceMeasurement(module)

    const request: any = {}
    request.Current = map(module, measurementData)

    if (referenceMeasurementData) {
      request.Characterization = map(module, referenceMeasurementData)
    }

    const response = await measurement[G.ADAPTER][G.HTTP].post({
      url: `/api/v${version}/apmData/pdf`,
      params: request,
    })

    const name = getFilename(request)

    eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.LOAD), { [G.DATA]: {
      item: { value: response, name, filename: name },
      type: 'application/pdf',
      useInternalStorage: true,
      saveToFiles: false,
    } })
  } catch (e) {
    console.error('Error while attempting to export report:', e)
    await showErrorDialog(module)
  } finally {
    component.loading = false
    await module[G.ADAPTER][G.UI].update(module)
  }
}

export default curry(exportReport)
